import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTheme } from '@nbx/frontend-helpers/subscribe';
import SEO from 'components/shared/SEO';
import MarketId from 'components/shared/MarketId';
import Text from 'components/shared/Text';
import Table from 'components/shared/Table';
import SVG from 'components/icons/SVG';
import ButtonText from 'components/shared/ButtonText';
import { useTradeActivity } from 'services';
import useAssets from 'hooks/useAssets';
import { getOptionsListOfAssets } from 'helpers/assets';
import { useTranslation, Trans } from 'react-i18next';
import { isWebApp } from 'helpers/platform';
import styles from './styles.module.scss';

const microapps = window.MICROFRONTENDS_CONFIG;

const columnWidths = [1.5, 0.5, 1.5, 1.5, 1.5, 2, 1.5, 2];

const getLast3Years = () => {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  date.setFullYear(date.getFullYear() - 3);
  return date;
};

const getLastMonth = () => {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  date.setMonth(date.getMonth() - 1);
  return date;
};

function TradeActivity(props) {
  const { t } = useTranslation();
  const [asset, setAsset] = useState('All');
  const [viewOptions, setViewOptions] = useState({});
  const [from, setFrom] = useState(getLastMonth());
  const [to, setTo] = useState(null);

  const headers = [
    t('common_market'),
    t('common_side'),
    t('common_order_id'),
    t('common_price'),
    t('common_amount'),
    t('common_fee'),
    t('common_total'),
    t('orders_filled_at')
  ];
  const csvHeaders = [
    { label: t('common_order_id'), key: 'orderId' },
    { label: t('common_market'), key: 'pair' },
    { label: t('common_side'), key: 'side' },
    { label: t('common_price'), key: 'settledPrice' },
    { label: t('common_amount'), key: 'settledQty' },
    { label: t('common_fee'), key: 'settledFee' },
    { label: t('common_total'), key: 'settledTotal' },
    { label: t('orders_filled_at'), key: 'filledAt' }
  ];

  const assets = useAssets();

  const trades = useTradeActivity({
    accountId: props.accountId,
    startTime: from,
    endTime: to,
    asset,
    assetsData: assets.data
  });

  const theme = useTheme();

  useEffect(() => {
    setViewOptions({ All: t('common_all'), ...getOptionsListOfAssets(assets.data, theme) });
  }, [assets.data, theme, t]);

  const orderLink = fill => `${microapps.routes.orders.path}/${fill.orderId}`;

  const generateReportName = () => {
    let filename = `trade-activity`;
    if (asset !== 'All') filename = `${filename}-${asset.toLowerCase()}`;
    if (from) filename = `${filename}-from-${from.toISOString().substring(0, 10)}`;
    if (to) filename = `${filename}-to-${to.toISOString().substring(0, 10)}`;
    return `${filename}.csv`;
  };

  const onChangeAsset = e => {
    setAsset(e.target.value);
  };

  const onFromChange = newFrom => {
    if (newFrom) {
      const date = new Date(newFrom);
      date.setHours(0, 0, 0, 0);
      setFrom(date > getLast3Years() ? date : getLast3Years());
    } else setFrom(getLastMonth());
  };

  const onToChange = newTo => {
    if (newTo) {
      const date = new Date(newTo);
      date.setHours(23, 59, 59, 999);
      setTo(date);
    } else setTo(null);
  };

  const refetchOnError = () => {
    if (trades.error) trades.refetch();
    if (assets.error) assets.refetch();
  };

  const renderItem = (item, index) =>
    ({
      0: <MarketId pair={item.pair} iconSize="20" />,
      1: (
        <Text
          styles={[
            {
              [styles.buy]: item.side === 'BUY',
              [styles.sell]: item.side === 'SELL'
            }
          ]}>
          {item.side === 'BUY' ? t('common_buy') : t('common_sell')}
        </Text>
      ),
      2: (
        <div className={styles.orderWrapper}>
          <Text title={item.orderId} styles={[styles.address]} monospaced>
            {`${item.orderId.substring(0, 5)}...${item.orderId.substring(
              item.orderId.length - 5,
              item.orderId.length
            )}`}{' '}
          </Text>
          <ButtonText to={orderLink(item)} title={t('orders_order_details')}>
            <SVG icon="info" muted position="relative" top="4px" />
          </ButtonText>
        </div>
      ),
      3: <Text monospaced>{item.settledPrice}</Text>,
      4: <Text monospaced>{item.settledQty}</Text>,
      5: <Text monospaced>{item.settledFee}</Text>,
      6: <Text monospaced>{item.settledTotal}</Text>,
      7: <Text monospaced>{item.filledAt}</Text>
    }[index]);

  return (
    <>
      <SEO title={t('orders_trade_activity')} />
      {!isWebApp() ? (
        <div className={styles.mobileMessageWrapper}>
          <Text weight="bold" styles={[styles.headerColumn]}>
            <Trans i18nKey="orders_trading_activity">Trading Activity</Trans>
          </Text>
          <Text muted>
            <Trans i18nKey="orders_please_sign_in">
              Please log in to NBX on a computer to view and and export your full trading activity logs.
            </Trans>
          </Text>
        </div>
      ) : (
        <Table
          keyField="key"
          columnWidths={columnWidths}
          data={trades.data || []}
          refetchData={refetchOnError}
          error={trades.error || assets.error}
          isLoading={trades.loading || assets.loading}
          headers={headers}
          renderItem={renderItem}
          view={asset}
          viewOptions={viewOptions}
          onViewChange={onChangeAsset}
          endAlignIndex={2}
          downloadable
          csvHeaders={csvHeaders}
          downloadFilename={generateReportName()}
          downloadButtonLabel={t('orders_download_report')}
          rowHeight={55}
          dateFiltering
          dateFrom={from}
          dateTo={to}
          onDateFromChange={onFromChange}
          onDateToChange={onToChange}
        />
      )}
    </>
  );
}

TradeActivity.propTypes = {
  accountId: PropTypes.string.isRequired
};

export default TradeActivity;
